import { Badge, Box, Button, ButtonGroup, IconButton, Menu, MenuButton, MenuItem, MenuList, Select, Spinner, Text, Tooltip, useBoolean } from '@chakra-ui/react'
import type { Offer } from 'BrokerTsLib/src/dtos'
import { customAddDays } from 'BrokerTsLib/src/extensions/date.extensions'
import { useState, useTransition } from 'react'
import { DatepickerPopup } from '../../components/DatepickerPopup'
import { SortBy } from '../../context/OffersProvider'
import { useApp } from '../../hooks/useApp'
import { useSetOfferIncluded } from '../../hooks/useOffer'
import { ColumnTriple, SelectArrowDown, Settings, ToggleRightFilled } from '../../icons'

const FilterOrderWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{children}</Box>
}

const Buttons = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignContent: 'start', gap: 2 }}>{children}</Box>
  )
}

const Order = ({ sortBy, changeSortBy }: { sortBy: SortBy, changeSortBy: (data: SortBy) => void }) => {
  const { app } = useApp()

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Text sx={{ fontWeight: 'medium' }}>Řazení</Text>

      <Select value={sortBy} onChange={(e) => changeSortBy(e.target.value as SortBy)} size="sm">
        <option value={SortBy.Price}>Dle pojistného</option>
        <option value={SortBy.Insurer}>Dle pojistitele</option>
        {app != 'cestovko' &&
          <>
            <option value={SortBy.VariantPrice}>Dle rozsahu a ceny</option>
            <option value={SortBy.VariantInsurer}>Dle rozsahu a pojistitele</option>
          </>
        }
      </Select>
    </Box>
  )
}

type OffersToolbarProps = {
  offers: Offer[]
  showAll: boolean
  setShowAll: Function
  editable: boolean
  showParticipation?: boolean
  showCompareButton?: boolean
  showListButton?: boolean
  onCompareClick?: Function
  onSettingsClick?: Function
  onSetStartDate?: (date: Date) => Promise<any>
  sortBy: SortBy
  changeSortBy: (data: SortBy) => void
  updateOffer: (offer: Offer) => void
}

export const OffersToolbar = ({
  offers,
  showAll,
  setShowAll,
  editable,
  showCompareButton = false,
  onCompareClick,
  showListButton,
  onSettingsClick,
  onSetStartDate,
  sortBy,
  changeSortBy,
  updateOffer
}: OffersToolbarProps) => {
  const { app } = useApp()

  const [isTransitioning, startTransition] = useTransition()

  const [showOnlyIncluded, setShowOnlyIncluded] = useBoolean(!showAll)

  const onChangeShowAll = () => {
    setShowAll(showOnlyIncluded)
    setShowOnlyIncluded.toggle()
  }

  const [startDate, setStartDate] = useState<Date | undefined>()
  const [isSavingInsruanceStartDate, setIsSavingInsruanceStartDate] = useState(false)

  const handleSetStartDate = (date: Date) => {
    setStartDate(date)

    if (onSetStartDate) {
      setIsSavingInsruanceStartDate(true)
      onSetStartDate(date)
        .finally(() => setIsSavingInsruanceStartDate(false))
    }
  }

  let labelSetInsuranceStart = 'Kliknutím nastavíte datum počátku na dnešní datum'
  let defaultStartDate = new Date()

  if (app == 'chaloupka') {
    labelSetInsuranceStart = 'Kliknutím nastavíte datum počátku na zítřešní datum'
    defaultStartDate = customAddDays(new Date(), 1)
    defaultStartDate.setHours(12, 0, 0, 0)
  }

  const { mutateAsync: setIncluded, isLoading } = useSetOfferIncluded()

  const setIncludeToAll = ((included: boolean) => {
    startTransition(() => {
      offers.forEach(offer => {
        if (offer.included == included) return

        offer.included = included
        updateOffer(offer)
        setIncluded({ offerId: offer.offerId!, included: included })
      })
    })
  })

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between' }}>
      <FilterOrderWrapper>
        {/* <Filter showParticipation={showParticipation} /> */}
        <Order sortBy={sortBy} changeSortBy={changeSortBy} />
      </FilterOrderWrapper>

      <Buttons>
        {editable &&
          <>
            {onSettingsClick &&
              <Button variant="outline" size="sm" onClick={() => onSettingsClick()}>
                <Settings color="secondary.500" />
                Parametry nabídky
              </Button>
            }

            {onSetStartDate &&
              <ButtonGroup isAttached>
                <Tooltip hasArrow label={labelSetInsuranceStart}>
                  <Button variant="outline" size="sm" mr="-px" onClick={() => handleSetStartDate(defaultStartDate)}>
                    Počátek pojištění
                  </Button>
                </Tooltip>

                <Tooltip hasArrow label="Kliknutím nastavíte datum počátku pomocí výběru v kalendáři">
                  <Button variant="outline" size="sm">
                    {!isSavingInsruanceStartDate &&
                      <DatepickerPopup
                        date={startDate}
                        onDateChange={(date: Date) => handleSetStartDate(date)} dateMin={new Date(defaultStartDate.toDateString())}
                      />}
                    {isSavingInsruanceStartDate && <Spinner />}
                  </Button>
                </Tooltip>
              </ButtonGroup>
            }
          </>
        }

        <ButtonGroup isAttached variant="outline" size='sm'>
          <Button onClick={onChangeShowAll}>
            {!showOnlyIncluded &&
              <>
                <ToggleRightFilled color="secondary.500" />
                Pouze vybrané
                <Badge>{offers.filter(x => x.included).length}</Badge>
              </>}

            {showOnlyIncluded &&
              <>
                <ToggleRightFilled color="gray.500" />
                Všechny
                <Badge>{offers.length}</Badge>
              </>}
          </Button>

          <Menu>
            <MenuButton as={IconButton} icon={<SelectArrowDown boxSize={3} />} px={2} isLoading={isTransitioning || isLoading} />
            <MenuList>
              <MenuItem gap={3} onClick={() => setIncludeToAll(false)}>
                <ToggleRightFilled color="gray.500" />
                Označit všechny nabídky jako neaktivní
              </MenuItem>
              <MenuItem gap={3} onClick={() => setIncludeToAll(true)}>
                <ToggleRightFilled color="secondary.500" />
                Označit všechny nabídky jako aktivní
              </MenuItem>
            </MenuList>
          </Menu>
        </ButtonGroup>

        {showCompareButton && (
          <Button
            colorScheme="primary"
            size="sm"
            onClick={() => onCompareClick && onCompareClick()}
          >
            <ColumnTriple color="white" />
            Porovnat
          </Button>
        )}

        {showListButton && (
          <Button
            colorScheme="primary"
            size="sm"
            onClick={() => onCompareClick && onCompareClick()}
          >
            <ColumnTriple color="white" />
            Zobrazit nabídky
          </Button>
        )}
      </Buttons>
    </Box>
  )
}
